import React, { useEffect } from 'react';
import { ArrowRight, Ruler, Home, Thermometer, Droplets, Wind, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';
import PageSEO from '@/components/PageSEO';
import { ReplacementPageLinks } from '@/components/InternalLinks';

const SIZING_TABLE = [
  { sqft: '800 – 1,200', tons: '1.5 – 2.0', btu: '18,000 – 24,000' },
  { sqft: '1,200 – 1,500', tons: '2.0 – 2.5', btu: '24,000 – 30,000' },
  { sqft: '1,500 – 2,000', tons: '2.5 – 3.0', btu: '30,000 – 36,000' },
  { sqft: '2,000 – 2,500', tons: '3.0 – 4.0', btu: '36,000 – 48,000' },
  { sqft: '2,500 – 3,000+', tons: '4.0 – 5.0 (max residential)', btu: '48,000 – 60,000' },
];

const FACTORS = [
  { title: 'Home Square Footage', desc: 'The primary factor in sizing. Larger homes need more cooling capacity.', icon: Home },
  { title: 'Ceiling Height', desc: 'Taller ceilings increase the volume of air that needs to be cooled.', icon: Ruler },
  { title: 'Insulation Quality', desc: 'Poor insulation means the system must work harder to maintain temperature.', icon: Thermometer },
  { title: 'Louisiana Humidity', desc: 'Southeast Louisiana humidity requires additional capacity for dehumidification.', icon: Droplets },
  { title: 'Window Exposure', desc: 'Homes with large south-facing windows absorb more heat, increasing load.', icon: Wind },
  { title: 'Ductwork Condition', desc: 'Leaky or undersized ducts reduce system efficiency and effective capacity.', icon: Wind },
];

const FAQ_DATA = [
  { q: 'What happens if my AC unit is too small?', a: 'An undersized AC will run continuously without adequately cooling your home. This leads to higher energy bills, excessive wear on components, poor humidity control, and a shorter system lifespan.' },
  { q: 'What happens if my AC unit is too large?', a: 'An oversized system will cool the air quickly but shut off before properly dehumidifying your home. This causes short cycling, uneven temperatures, higher humidity indoors, and increased equipment wear.' },
  { q: 'How is AC tonnage determined?', a: 'AC tonnage is determined by a Manual J load calculation that considers square footage, insulation, window area, ceiling height, climate zone, and other factors. One ton of cooling equals 12,000 BTU per hour.' },
  { q: 'Should I size up for Louisiana heat?', a: 'A proper load calculation already accounts for local climate conditions including Louisiana heat and humidity. Oversizing beyond the calculation is not recommended and can cause performance problems.' },
];

const AcSizingPage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'faq');
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_DATA.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PageSEO breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'What Size AC Unit Do I Need', url: '/what-size-ac-unit-do-i-need' },
      ]} />
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white" data-testid="ac-sizing-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="ac-sizing-heading">
            What Size AC Unit Do I Need?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            Choosing the right AC size is one of the most important decisions when replacing your HVAC system.
            An improperly sized unit wastes energy, reduces comfort, and shortens equipment life.
          </p>
          <p className="text-base text-slate-500 max-w-2xl mx-auto mb-8">
            This guide explains how HVAC sizing works and what Louisiana homeowners should know before requesting estimates.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button data-testid="hero-cta" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get Your Instant HVAC Estimate <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Sizing Chart */}
      <section className="py-16" data-testid="sizing-chart-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Ruler className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              General AC Sizing Guide
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Use this chart as a starting reference. Actual sizing requires a professional load calculation.
          </p>
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
            <div className="grid grid-cols-3 bg-[#0F172A] text-white text-sm font-bold">
              <div className="p-4">Home Size (sq ft)</div>
              <div className="p-4">AC Size (tons)</div>
              <div className="p-4">Cooling Capacity (BTU/hr)</div>
            </div>
            {SIZING_TABLE.map((row) => (
              <div key={row.sqft} className="grid grid-cols-3 text-sm border-b border-slate-100 last:border-0">
                <div className="p-4 text-[#0F172A] font-medium">{row.sqft}</div>
                <div className="p-4 text-slate-600">{row.tons}</div>
                <div className="p-4 text-slate-600">{row.btu}</div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4 italic">
            These ranges are approximate. Louisiana homes may require larger systems due to high humidity and heat load. A Manual J calculation is recommended for accurate sizing.
          </p>
        </div>
      </section>

      {/* Factors */}
      <section className="py-16 bg-slate-50" data-testid="factors-section">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Factors That Affect AC Sizing
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {FACTORS.map((f) => (
              <div key={f.title} className="bg-white p-6 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center">
                    <f.icon className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                </div>
                <p className="text-sm text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sizing Matters */}
      <section className="py-16" data-testid="why-sizing-matters">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Proper Sizing Matters in Louisiana
          </h2>
          <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
            <p className="text-slate-600 text-base leading-relaxed">
              Louisiana consistently ranks among the most humid states in the country. Your AC system doesn't just cool the air — it removes moisture. An improperly sized system can't do both effectively.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              A professional contractor will perform a <strong>Manual J load calculation</strong> that accounts for your specific home's construction, orientation, insulation, ductwork, and local climate data to recommend the right system size.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50" data-testid="ac-sizing-faq">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQ_DATA.map((faq) => (
              <div key={faq.q} className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{faq.q}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReplacementPageLinks />

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Find Out What Size System Your Home Needs
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Use our free tool to get an instant estimate based on your home size and system preferences.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button data-testid="cta-estimate" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-10 rounded-full text-lg orange-glow transition-all duration-300 transform hover:-translate-y-1">
              Get Your Instant HVAC Estimate <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <RelatedArticles currentPath="/what-size-ac-unit-do-i-need" />
      <Footer />
    </div>
  );
};

export default AcSizingPage;
